import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { isPastSelection } from '../lib/format'
import type {
  BookingInfo,
  CalculateAmount,
  Cart,
  TransportContext,
} from '../lib/api/types'

const emptyCart: Cart = {
  transportId: null,
  fromDay: null,
  fromHour: null,
  toDay: null,
  durationId: null,
  rateId: null,
  conventionId: null,
  serviceIdList: null,
  couponCode: null,
  paymentModeId: null,
}

const emptyBookingInfo: BookingInfo = {
  name: '',
  lastname: '',
  email: '',
  phoneNumber: '',
  country: 'IT',
  licensePlate: '',
  privacy01: '',
  privacy02: '',
  privacy03: '',
  requireBilling: '',
}

export interface ConfirmationResult {
  code: string
  title?: string
  text?: string
}

interface BookingState {
  contexts: TransportContext[]
  cart: Cart
  calculatedInfo: CalculateAmount | null
  bookingInfo: BookingInfo
  couponLabel: string | null
  conventionLabel: string | null
  // Set on a successful booking; read by the confirmation step. Transient (not persisted).
  confirmation: ConfirmationResult | null

  setContexts: (c: TransportContext[]) => void
  setTransport: (transportId: number) => void
  setDates: (fromDay: string, fromHour: string, toDay: string) => void
  clearStaleDates: () => boolean
  setRate: (rateId: number | null) => void
  setConvention: (conventionId: number | null, label?: string | null) => void
  toggleService: (serviceId: number) => void
  setCoupon: (code: string | null, label: string | null) => void
  setPaymentMode: (paymentModeId: number | null) => void
  setCalculated: (c: CalculateAmount | null) => void
  setBookingInfo: (patch: Partial<BookingInfo>) => void
  setConfirmation: (c: ConfirmationResult | null) => void
  reset: () => void

  contextFor: (transportId: number | null) => TransportContext | undefined
}

export const useBooking = create<BookingState>()(
  persist(
    (set, get) => ({
      contexts: [],
      cart: { ...emptyCart },
      calculatedInfo: null,
      bookingInfo: { ...emptyBookingInfo },
      couponLabel: null,
      conventionLabel: null,
      confirmation: null,

      setContexts: (contexts) => set({ contexts }),

      setTransport: (transportId) =>
        set((s) => ({
          // changing transport invalidates rate/services selection
          cart: { ...s.cart, transportId, rateId: null, serviceIdList: null, conventionId: null },
          conventionLabel: null,
        })),

      setDates: (fromDay, fromHour, toDay) =>
        set((s) => ({ cart: { ...s.cart, fromDay, fromHour, toDay, rateId: null } })),

      /**
       * The cart is persisted to localStorage with no expiry, so someone who started a booking days
       * ago comes back with an arrival date already in the past. The date picker can't produce one
       * (past days are disabled), so it would never be corrected by hand: the funnel would just keep
       * posting the dead date and the backend would keep answering "FromDay is invalid".
       * Returns true when something was cleared, so the caller can send the user back to the dates step.
       */
      clearStaleDates: () => {
        const { fromDay, fromHour } = get().cart
        if (!isPastSelection(fromDay, fromHour)) return false
        set((s) => ({
          cart: { ...s.cart, fromDay: null, fromHour: null, toDay: null, rateId: null },
          calculatedInfo: null,
        }))
        return true
      },

      setRate: (rateId) => set((s) => ({ cart: { ...s.cart, rateId } })),

      setConvention: (conventionId, label = null) =>
        set((s) => ({ cart: { ...s.cart, conventionId }, conventionLabel: conventionId ? label : null })),

      toggleService: (serviceId) =>
        set((s) => {
          const list = new Set(s.cart.serviceIdList ?? [])
          if (list.has(serviceId)) list.delete(serviceId)
          else list.add(serviceId)
          const arr = [...list]
          return { cart: { ...s.cart, serviceIdList: arr.length ? arr : null } }
        }),

      setCoupon: (couponCode, couponLabel) =>
        set((s) => ({ cart: { ...s.cart, couponCode }, couponLabel })),

      setPaymentMode: (paymentModeId) => set((s) => ({ cart: { ...s.cart, paymentModeId } })),

      setCalculated: (calculatedInfo) => set({ calculatedInfo }),

      setBookingInfo: (patch) => set((s) => ({ bookingInfo: { ...s.bookingInfo, ...patch } })),

      /**
       * A finished booking must not leave its cart behind. The cart is persisted to localStorage
       * with no expiry, so the next booking would silently start with the previous dates, services
       * and — worst — a coupon that has already been spent, which the server then strips and the
       * customer sees a discount that quietly does not apply.
       *
       * reset() only ran if the customer happened to tap the button on the confirmation screen;
       * closing the app instead left everything in place for ever. The personal details in
       * bookingInfo are kept on purpose, so a repeat booking does not have to be retyped.
       */
      setConfirmation: (confirmation) =>
        set(() =>
          confirmation
            ? {
                confirmation,
                cart: { ...emptyCart },
                calculatedInfo: null,
                couponLabel: null,
                conventionLabel: null,
              }
            : { confirmation },
        ),

      reset: () =>
        set({
          cart: { ...emptyCart },
          calculatedInfo: null,
          bookingInfo: { ...emptyBookingInfo },
          couponLabel: null,
          conventionLabel: null,
          confirmation: null,
        }),

      contextFor: (transportId) => get().contexts.find((c) => c.transport.id === transportId),
    }),
    {
      name: 'gsm-booking',
      partialize: (s) => ({ cart: s.cart, bookingInfo: s.bookingInfo, couponLabel: s.couponLabel, conventionLabel: s.conventionLabel }),
      // Drop an expired selection the moment the persisted cart comes back, before any screen reads it.
      onRehydrateStorage: () => (state) => {
        state?.clearStaleDates()
      },
    },
  ),
)

/** True when the cart has enough to compute a price. */
export function cartIsCalculable(cart: Cart): boolean {
  return Boolean(cart.transportId && cart.fromDay && cart.fromHour && (cart.toDay || cart.rateId))
}
